// @ts-check

import cloneDeep from 'lodash/cloneDeep';
import defaults from 'lodash/defaults';
import {createStore} from 'redux';

import {
    ADD_OBJECTS,
    ADD_SELECTION,
    REMOVE_OBJECTS,
    REMOVE_SELECTION,
    SET_DROPDOWN_METADATA,
    SET_EDITING,
    SET_MOVE_DIALOG_OPEN,
    SET_NAME,
    SET_OBJECTS,
    SET_PATH,
    SET_PERMISSIONS,
    SET_REMOVE_DIALOG_OPEN,
    SET_SEARCH,
    SET_SELECTION,
    SET_SYNC,
    UPDATE_CHILDREN,
} from './folderui-actions';

const DEFAULT_DROPDOWN = {
    sync: false,
    public: false,
    count: undefined,
};

const DEFAULT_SEARCH = {
    string: '',
};

/**
 * Handle SET_SELECTION, ADD_SELECTION and REMOVE_SELECTION.
 *
 * @param {Set<string>} state - Set of ids.
 * @param {import("redux").AnyAction} action - Action to modify selection with.
 */
const selection = (state = new Set([]), action) => {
    const newSet = new Set(state);

    switch (action.type) {
        case SET_SELECTION:
            return new Set(action.ids);
        case ADD_SELECTION:
            action.ids.forEach((/** @type {string} */ id) => {
                newSet.add(id);
            });

            return newSet;
        case REMOVE_SELECTION:
            action.ids.forEach((/** @type {string} */ id) => {
                newSet.delete(id);
            });

            return newSet;
        default:
            return state;
    }
};

/**
 * Handle SET_DROPDOWN_METADATA.
 * @typedef DropdownState
 * @prop {boolean} sync
 * @prop {boolean} public
 * @prop {number} [count]
 *
 * @param  {DropdownState} state
 * @param {import("redux").AnyAction} action
 */
const dropdown = (state = DEFAULT_DROPDOWN, action) => {
    switch (action.type) {
        case SET_DROPDOWN_METADATA:
            return {...state, ...action.metadata};
        default:
            return state;
    }
};

/**
 * Handle SET_MOVE_DIALOG_OPEN for MoveDialog
 * @param {import("redux").AnyAction} action
 */
const moveDialog = (state = {open: false}, action) => {
    switch (action.type) {
        case SET_MOVE_DIALOG_OPEN:
            return {...state, ...action.state};
        default:
            return state;
    }
};

/**
 * Handle SET_REMOVE_DIALOG_OPEN for ConfirmationDialog
 * @param {import("redux").AnyAction} action
 */
const confirmationDialog = (state = {open: false}, action) => {
    switch (action.type) {
        case SET_REMOVE_DIALOG_OPEN:
            return {...state, ...action.state};
        default:
            return state;
    }
};

/**
 * Handle SET_SEARCH.
 * @param {import("redux").AnyAction} action
 */
const search = (state = DEFAULT_SEARCH, action) => {
    switch (action.type) {
        case SET_SEARCH:
            return {...state, string: action.string};
        default:
            return state;
    }
};

/**
 * Handle SET_PATH.
 * @param {string[]} state
 * @param {import("redux").AnyAction} action
 */
const path = (state = [], action) => {
    switch (action.type) {
        case SET_PATH:
            return action.path;
        default:
            return state;
    }
};

/**
 * HANDLE SET_OBJECTS, ADD_OBJECTS, REMOVE_OBJECTS.
 * @param {any} state
 * @param {any} selectionState
 * @param {import("redux").AnyAction} action
 */
const objects = (state = {}, selectionState, action) => {
    const clonedState = cloneDeep(state);

    switch (action.type) {
        case SET_OBJECTS:
            return action.objects;
        case ADD_OBJECTS:
            defaults(clonedState, action.objects);
            clonedState[action.id].children = action.children;

            return clonedState;
        case REMOVE_OBJECTS:
            selectionState.forEach((/** @type {string | number} */ id) => {
                // Delete the Object
                delete clonedState[id];
                // Remove its id where it's listed as a child
                if (action.parentID) {
                    const index = clonedState[action.parentID].children.indexOf(id);
                    clonedState[action.parentID].children.splice(index, 1);
                }
            });
            return clonedState;
        case UPDATE_CHILDREN:
            selectionState.forEach((/** @type {any} */ id) => {
                // Remove its id where it's listed as a child
                if (action.parentID) {
                    const index = clonedState[action.parentID].children.indexOf(id);
                    clonedState[action.parentID].children.splice(index, 1);
                }
                // Add the id in the children list of the destination folder
                if (action.destType === 'folder') {
                    /*
                    topFolders contains the new folder
                    but updating the children field (not existant)
                    won't update the state and thus not the UI.
                    */
                    // action.topFolders[action.destID].children.push(id)
                    /*
                    clonedState does only contain the current objects
                    (the ones used to build the view)
                    This might need to change ?
                    */
                    // clonedState[action.destID].children.push(id)
                }
            });
            return clonedState;
        case SET_SYNC:
            selectionState.forEach((/** @type {string | number} */ id) => {
                clonedState[id].sync = action.sync;
            });

            return clonedState;
        case SET_EDITING:
            selectionState.forEach((/** @type {string | number} */ id) => {
                clonedState[id].editing = action.editing;
            });

            return clonedState;
        case SET_PERMISSIONS:
            selectionState.forEach((/** @type {string | number} */ id) => {
                clonedState[id].public = action.permissions;
            });

            return clonedState;
        case SET_NAME:
            clonedState[action.id].name = action.name;

            return clonedState;
        default:
            return state;
    }
};

class Store {
    /**
     * Assign methods to manage sub-state.
     */
    constructor() {
        this.store = createStore((state = {}, action) => ({
            objects: objects(state.objects, state.selection, action),
            path: path(state.path, action),
            selection: selection(state.selection, action),
            dropdown: dropdown(state.dropdown, action),
            moveDialog: moveDialog(state.moveDialog, action),
            confirmationDialog: confirmationDialog(state.confirmationDialog, action),
            search: search(state.search, action),
        }));
    }
}

export default Store;
